/**
 * Rule evaluation jobs feature reducer.
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { fetchJobs, fetchJob, triggerJob, fetchJobResults } from '../actions/jobsThunks';
import { ApiErrorPayload } from '../facades/apiFacade';
import { RuleEvaluationJob, RuleEvaluationResult } from '../../@types/jobs';

type LoadStatus = 'idle' | 'loading' | 'loaded' | 'error';

export interface JobsListState {
    status: LoadStatus;
    data: RuleEvaluationJob[];
    error: ApiErrorPayload | null;
}

export interface JobResultsState {
    status: LoadStatus;
    data: RuleEvaluationResult[];
    error: ApiErrorPayload | null;
}

export interface ProjectJobsState {
    jobs: JobsListState;
    /** Keyed by job object_id. */
    results: Record<string, JobResultsState>;
    trigger: { isSaving: boolean; error: ApiErrorPayload | null };
}

export interface JobsState {
    /** Keyed by project object_id. */
    byProject: Record<string, ProjectJobsState>;
}

const emptyProjectJobs: ProjectJobsState = {
    jobs: { status: 'idle', data: [], error: null },
    results: {},
    trigger: { isSaving: false, error: null },
};

const initialState: JobsState = {
    byProject: {},
};

function projectEntry(state: JobsState, projectId: string): ProjectJobsState {
    if (!state.byProject[projectId]) {
        state.byProject[projectId] = JSON.parse(JSON.stringify(emptyProjectJobs));
    }
    return state.byProject[projectId];
}

function upsertJob(jobs: RuleEvaluationJob[], job: RuleEvaluationJob): RuleEvaluationJob[] {
    const index = jobs.findIndex((j) => j.object_id === job.object_id);
    if (index === -1) return [job, ...jobs];
    const next = [...jobs];
    next[index] = job;
    return next;
}

export const jobsSlice = createSlice({
    name: 'jobs',
    initialState,
    reducers: {
        clearTriggerError(state, action: PayloadAction<string>) {
            const entry = state.byProject[action.payload];
            if (entry) entry.trigger.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchJobs.pending, (state, action) => {
                const entry = projectEntry(state, action.meta.arg.projectId);
                entry.jobs.status = 'loading';
                entry.jobs.error = null;
            })
            .addCase(fetchJobs.fulfilled, (state, action) => {
                const entry = projectEntry(state, action.payload.projectId);
                entry.jobs.status = 'loaded';
                entry.jobs.data = action.payload.jobs;
            })
            .addCase(fetchJobs.rejected, (state, action) => {
                const entry = projectEntry(state, action.payload?.projectId ?? action.meta.arg.projectId);
                entry.jobs.status = 'error';
                entry.jobs.error = action.payload ?? { message: 'Failed to load checks.' };
            })

            // A single job's poll tick. Deliberately does not touch
            // `jobs.status`/`jobs.error` — those belong to `fetchJobs`, and a
            // poll failing on one tick should not blank out an already-loaded
            // list or show a page-level error.
            .addCase(fetchJob.fulfilled, (state, action) => {
                const entry = projectEntry(state, action.payload.projectId);
                entry.jobs.data = upsertJob(entry.jobs.data, action.payload.job);
            })

            .addCase(triggerJob.pending, (state, action) => {
                const entry = projectEntry(state, action.meta.arg.projectId);
                entry.trigger.isSaving = true;
                entry.trigger.error = null;
            })
            .addCase(triggerJob.fulfilled, (state, action) => {
                const entry = projectEntry(state, action.payload.projectId);
                entry.trigger.isSaving = false;
                entry.jobs.data = upsertJob(entry.jobs.data, action.payload.job);
            })
            .addCase(triggerJob.rejected, (state, action) => {
                const entry = projectEntry(state, action.payload?.projectId ?? action.meta.arg.projectId);
                entry.trigger.isSaving = false;
                entry.trigger.error = action.payload ?? { message: 'Failed to trigger a check.' };
            })

            .addCase(fetchJobResults.pending, (state, action) => {
                const entry = projectEntry(state, action.meta.arg.projectId);
                const results = entry.results[action.meta.arg.jobId] ?? { status: 'idle', data: [], error: null };
                results.status = 'loading';
                results.error = null;
                entry.results[action.meta.arg.jobId] = results;
            })
            .addCase(fetchJobResults.fulfilled, (state, action) => {
                const entry = projectEntry(state, action.payload.projectId);
                entry.results[action.payload.jobId] = {
                    status: 'loaded',
                    data: action.payload.results,
                    error: null,
                };
            })
            .addCase(fetchJobResults.rejected, (state, action) => {
                const projectId = action.payload?.projectId ?? action.meta.arg.projectId;
                const entry = projectEntry(state, projectId);
                entry.results[action.meta.arg.jobId] = {
                    status: 'error',
                    data: [],
                    error: action.payload ?? { message: 'Failed to load check results.' },
                };
            });
    },
});

export const { clearTriggerError } = jobsSlice.actions;
export const jobsReducer = jobsSlice.reducer;
